import React, {
    useEffect,
    useState,
} from "react";
import {
    loadConfigVersion,
    loadConfigVersions,
} from "../api/configurations";
import {
    loadReportPlan as loadReportPlanRequest,
} from "../api/reportPlans";
import {
    reportToast,
} from "../../../../components/notifications/appToast";
import {
    applyReportPlanToPlan,
    clone,
} from "../reportConfigurationLogic";
import type {
    ChangeEntry,
    PlanConfig,
    ReportConfig,
    ReportConfigurationConfigVersionDetailDto,
    ReportConfigurationConfigVersionSummaryDto,
    ReportConfigurationReportPlanDto,
    ReportConfigurationState,
    ReportFolder,
} from "../types";
import {
    rebindReportConfigurationPayload,
} from "../reportConfigurationPersistence";

type ConfigurationLoadingInputs = {
    clientsLoaded: boolean;
    setData: React.Dispatch<React.SetStateAction<ReportConfigurationState>>;
    selectedPlanId: string;
    selectedFolderId: string;
    setSelectedFolderId: React.Dispatch<React.SetStateAction<string>>;
    selectedReportId: string;
    setSelectedReportId: React.Dispatch<React.SetStateAction<string>>;
    setExpandedFolders: React.Dispatch<React.SetStateAction<Set<string>>>;
    setSavedReportBaselines: React.Dispatch<React.SetStateAction<Record<string, { plan: PlanConfig; folder: ReportFolder; report: ReportConfig; }>>>;
    selectedPlan: PlanConfig;
    selectedFolder: ReportFolder;
    selectedReport: ReportConfig | undefined;
    applyReportConfigurationToState: (current: ReportConfigurationState, targetPlanId: string, targetFolderId: string, targetReportId: string, nextPlan: PlanConfig, nextFolder: ReportFolder, nextReport: ReportConfig, changeEntry?: ChangeEntry | undefined) => ReportConfigurationState;
};

export const useConfigurationLoading = ({
    clientsLoaded,
    setData,
    selectedPlanId,
    selectedFolderId,
    setSelectedFolderId,
    selectedReportId,
    setSelectedReportId,
    setExpandedFolders,
    setSavedReportBaselines,
    selectedPlan,
    selectedFolder,
    selectedReport,
    applyReportConfigurationToState,
}: ConfigurationLoadingInputs) => {
    const [reportPlanLoading, setReportPlanLoading] = useState(false);
    const [reportPlanLoadedForId, setReportPlanLoadedForId] = useState("");
    const [reportPlanError, setReportPlanError] = useState("");
    const [configLoadLoading, setConfigLoadLoading] = useState(false);

    useEffect(() => {
        const clientId = selectedPlan?.clientId;
        if (!clientId || !clientsLoaded) return;

        let isMounted = true;
        const request = new AbortController();

        const loadReportPlan = async () => {
            setReportPlanLoading(true);
            setReportPlanError("");

            try {
                const response = await loadReportPlanRequest(clientId, request.signal);
                if (!isMounted) return;

                const folders = Array.isArray(response?.folders) ? response.folders : [];
                const currentFolderStillExists = folders.some((folder) => `report_folder_${folder.reportFolderId}` === selectedFolderId);
                const nextFolderDto = currentFolderStillExists
                    ? folders.find((folder) => `report_folder_${folder.reportFolderId}` === selectedFolderId)
                    : folders[0];
                const currentReportStillExists = nextFolderDto?.reports?.some((report) => `report_${report.reportId}` === selectedReportId);
                const nextReportDto = currentReportStillExists
                    ? nextFolderDto?.reports.find((report) => `report_${report.reportId}` === selectedReportId)
                    : nextFolderDto?.reports?.[0];
                const nextFolderId = nextFolderDto ? `report_folder_${nextFolderDto.reportFolderId}` : "";
                const nextReportId = nextReportDto ? `report_${nextReportDto.reportId}` : "";

                setData((current) => ({
                    ...current,
                    plans: current.plans.map((plan) => plan.id === selectedPlanId ? applyReportPlanToPlan(plan, response) : plan),
                }));
                setReportPlanLoadedForId(selectedPlanId);
                setSelectedFolderId(nextFolderId);
                setSelectedReportId(nextReportId);
                setExpandedFolders(new Set(nextFolderId ? [nextFolderId] : []));
            } catch (error) {
                if (isMounted) {
                    console.log(error);
                    const message = "Unable to load report folders and reports for the selected health plan.";
                    setReportPlanError(message);
                    reportToast.error("Report tree did not load", reportToast.messageFromError(error, message));
                }
            } finally {
                if (isMounted) {
                    setReportPlanLoading(false);
                }
            }
        };

        loadReportPlan();

        return () => {
            isMounted = false;
            request.abort();
        };
    }, [clientsLoaded, selectedPlanId, selectedPlan?.clientId]);

    useEffect(() => {
        const reportId = selectedReport?.reportId;
        if (!reportId || !selectedFolder || !selectedReport) {
            setConfigLoadLoading(false);
            return;
        }

        let isMounted = true;
        const request = new AbortController();
        const targetPlan = selectedPlan;
        const targetFolder = selectedFolder;
        const targetReport = selectedReport;
        const targetPlanId = selectedPlan.id;
        const targetFolderId = selectedFolder.id;
        const targetReportId = selectedReport.id;

        const loadLatestConfiguration = async () => {
            setConfigLoadLoading(true);
            try {
                const response = await loadConfigVersions(reportId, request.signal);
                if (!isMounted) return;

                const versions = (Array.isArray(response) ? response : []) as ReportConfigurationConfigVersionSummaryDto[];
                const latestVersion = versions[0];

                if (!latestVersion?.configVersionId) {
                    setSavedReportBaselines((current) => ({
                        ...current,
                        [targetReportId]: {
                            plan: clone(targetPlan),
                            folder: clone(targetFolder),
                            report: clone(targetReport),
                        },
                    }));
                    return;
                }

                const detail = await loadConfigVersion(latestVersion.configVersionId, request.signal);
                if (!isMounted) return;

                if (detail.reportId && detail.reportId !== reportId) {
                    throw new Error("The saved configuration does not belong to the selected report.");
                }

                const rebound = rebindReportConfigurationPayload(targetPlan, targetFolder, targetReport, detail);
                setSavedReportBaselines((current) => ({
                    ...current,
                    [targetReportId]: {
                        plan: clone(rebound.plan),
                        folder: clone(rebound.folder),
                        report: clone(rebound.report),
                    },
                }));
                setData((current) => applyReportConfigurationToState(
                    current,
                    targetPlanId,
                    targetFolderId,
                    targetReportId,
                    rebound.plan,
                    rebound.folder,
                    rebound.report,
                ));
            } catch (error) {
                if (isMounted) {
                    console.log(error);
                    const message = "Unable to load the saved report configuration.";
                    reportToast.error("Saved configuration did not load", reportToast.messageFromError(error, message));
                }
            } finally {
                if (isMounted) {
                    setConfigLoadLoading(false);
                }
            }
        };

        loadLatestConfiguration();

        return () => {
            isMounted = false;
            request.abort();
        };
    }, [selectedPlan.id, selectedFolder?.id, selectedReport?.id, selectedReport?.reportId]);
    return {
        reportPlanLoading,
        reportPlanLoadedForId,
        reportPlanError,
        setReportPlanError,
        configLoadLoading,
    };
};
